import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";

import { translate } from "../../common/providers";
import { exportView } from "../../navigation";
import { setUserProfilePicture, clearCurrentUserProfilePictureCache } from "../../common/slice/contextSlice";
import { MyService, ApiService, FeatureFlagService } from "../../api";
import {
	CustomButton,
	ViewBanner,
	createNotification,
	UserAvatar,
	clearProfilePictureCache,
	resetCurrentUserProfilePictureFetch,
} from "../../common/components";

import { UpdateProfileDialog } from "./components";
import styles from "./Profile.module.css";

const Profile = () => {
	const [openUpdateProfile, setOpenUpdateProfile] = useState(false);
	const [isProfilePictureEnabled, setIsProfilePictureEnabled] = useState(false);
	const cancelTokenSourceRef = useRef(null);
	const dispatch = useDispatch();
	const user = useSelector(({ context }) => context.user);
	const profilePicture = useSelector((state) => state.context.user?.profilePictureUrl);
	const language = useSelector(({ context }) => context.language.value);
	const displayName = useMemo(
		() => user.displayName || `${user.firstName || ""} ${user.lastName || ""}`.trim() || user.email,
		[user]
	);
	const avatarClassName = useMemo(
		() => (profilePicture ? styles.avatar : `${styles.avatar} ${styles.avatarWithBorder}`),
		[profilePicture]
	);
	useEffect(() => {
		const documentTitle = translate("profile.document.title");
		document.title = documentTitle;
		cancelTokenSourceRef.current = ApiService.getCancelTokenSource();
		FeatureFlagService.getAll(cancelTokenSourceRef.current.token)
			.then((response) => {
				setIsProfilePictureEnabled(response?.PROFILE_PICTURE === true);
			})
			.catch(() => {
				setIsProfilePictureEnabled(false);
			});
		return () => {
			ApiService.cancelTokens(cancelTokenSourceRef.current);
		};
	}, []);

	const handleResetPassword = () => {
		MyService.changePasswordSso({ locale: language }, cancelTokenSourceRef.current.token)
			.then(() => {
				createNotification({
					type: "success",
					message: translate("common:reset-password-sso.notification.password-changed"),
				});
			})
			.catch((err) => {
				console.error(err);
			});
	};
	const handleOpenUpdateProfile = useCallback(() => {
		setOpenUpdateProfile((prev) => !prev);
	}, []);
	const handleDeleteProfilePicture = useCallback(() => {
		dispatch(setUserProfilePicture(null));
		dispatch(clearCurrentUserProfilePictureCache());
		clearProfilePictureCache();
		resetCurrentUserProfilePictureFetch();
		MyService.deleteProfilePicture(ApiService.getCancelTokenSource().token)
			.then(() => {
				createNotification({
					type: "success",
					message: translate("profile.profile-picture.success.deleted"),
				});
			})
			.catch((error) => {
				if (error && !error.toString().includes("404")) {
					console.error("Error deleting profile picture:", error);
					createNotification({
						type: "error",
						message: translate("profile.profile-picture.error.delete-failed"),
					});
				}
			});
	}, [dispatch]);
	return (
		<>
			<ViewBanner titles={[{ title: translate("navigation:home.edit-my-profile") }]} />
			<div className={styles.profileContainer}>
				<div className={styles.profileBanner}>
					<div className={styles.avatarContainer}>
						<UserAvatar
							isCurrentUser
							className={avatarClassName}
							displayName={displayName}
							email={user.email}
							firstName={user.firstName}
							lastName={user.lastName}
							size="profile"
							userId={user.id}
						/>
					</div>
					<div className={styles.userInfo}>
						<div className={styles.userName}>{displayName}</div>
						<div className={styles.userEmail}>
							{translate("profile.info.email")} : {user.email}
						</div>
					</div>
				</div>
				<div className={styles.actions}>
					<CustomButton color="secondary" size="md" variant="outlined" onClick={handleOpenUpdateProfile}>
						{translate("profile.update-profile.btn")}
					</CustomButton>
					<CustomButton color="secondary" size="md" variant="outlined" onClick={handleResetPassword}>
						{translate("profile.change-pwd.btn")}
					</CustomButton>
				</div>
			</div>
			<UpdateProfileDialog
				isProfilePictureEnabled={isProfilePictureEnabled}
				open={openUpdateProfile}
				profilePicture={profilePicture}
				user={user}
				onClose={handleOpenUpdateProfile}
				onDeletePhoto={handleDeleteProfilePicture}
			/>
		</>
	);
};

export default exportView({
	path: "/profile",
	component: Profile,
	localesPath: "/profile/locales",
});
